import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Clock, Check, X, Star } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function Transactions() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      toast.error(t('errors.transactionFailed'));
    }
  };

  const handleUpdateTransaction = async (transactionId, status) => {
    try {
      await axios.put(`${API}/transactions/${transactionId}`, { status }, { withCredentials: true });
      toast.success(t('notifications.transactionCompleted'));
      fetchTransactions();
    } catch (error) {
      console.error('Error updating transaction:', error);
      toast.error(t('errors.transactionFailed'));
    }
  };

  const handleSubmitReview = async () => {
    if (!comment.trim()) {
      toast.error(t('errors.invalidInput'));
      return;
    }

    setLoading(true);
    try {
      const revieweeId = selectedTransaction.requester_user_id === user.id 
        ? selectedTransaction.provider_user_id 
        : selectedTransaction.requester_user_id;

      await axios.post(`${API}/reviews`, {
        transaction_id: selectedTransaction.id,
        reviewee_id: revieweeId,
        rating,
        comment
      }, { withCredentials: true });

      toast.success(t('notifications.reviewSubmitted'));
      setShowReviewDialog(false);
      setComment('');
      setRating(5);
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = (status) => {
    if (status === 'all') return transactions;
    return transactions.filter(t => t.status === status);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'accepted': 'bg-blue-100 text-blue-700 border-blue-300',
      'completed': 'bg-green-100 text-green-700 border-green-300',
      'cancelled': 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[status] || colors.pending;
  };

  const TransactionCard = ({ transaction }) => {
    const isProvider = transaction.provider_user_id === user?.id;
    const canAccept = isProvider && transaction.status === 'pending';
    const canComplete = isProvider && transaction.status === 'accepted';
    const canReview = transaction.status === 'completed';

    return (
      <Card className="p-6 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Badge className={`px-3 py-1 border ${getStatusColor(transaction.status)}`}>
                {transaction.status}
              </Badge>
              <span className="font-bold text-teal-500 text-lg">{transaction.hours}h</span>
            </div>
            <p className="text-gray-900 font-medium mb-1">{transaction.description}</p>
            <p className="text-sm text-gray-500">
              {isProvider ? t('transactions.requestedBy') : t('transactions.providing')} • {new Date(transaction.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {canAccept && (
            <>
              <Button
                data-testid={`accept-transaction-${transaction.id}`}
                onClick={() => handleUpdateTransaction(transaction.id, 'accepted')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Check className="mr-2 h-4 w-4" />
                {t('transactions.accept')}
              </Button>
              <Button
                data-testid={`reject-transaction-${transaction.id}`}
                variant="outline"
                onClick={() => handleUpdateTransaction(transaction.id, 'cancelled')}
                className="border-red-500 text-red-500 hover:bg-red-50"
              >
                <X className="mr-2 h-4 w-4" />
                {t('transactions.decline')}
              </Button>
            </>
          )}
          {canComplete && (
            <Button
              data-testid={`complete-transaction-${transaction.id}`}
              onClick={() => handleUpdateTransaction(transaction.id, 'completed')}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              <Check className="mr-2 h-4 w-4" />
              {t('transactions.markComplete')}
            </Button>
          )}
          {canReview && (
            <Button
              data-testid={`review-transaction-${transaction.id}`}
              onClick={() => {
                setSelectedTransaction(transaction);
                setShowReviewDialog(true);
              }}
              variant="outline"
              className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
            >
              <Star className="mr-2 h-4 w-4" />
              {t('transactions.leaveReview')}
            </Button>
          )}
        </div>
      </Card>
    );
  };

  return (
    <Layout>
      <div data-testid="transactions-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900">{t('transactions.title')}</h1>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-teal-500" />
            <span className="font-semibold text-gray-700">{user?.credits_balance} {t('dashboard.credits')}</span>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="all" data-testid="tab-all">{t('transactions.tabs.all')}</TabsTrigger>
            <TabsTrigger value="pending" data-testid="tab-pending">{t('transactions.tabs.pending')}</TabsTrigger>
            <TabsTrigger value="accepted" data-testid="tab-accepted">{t('transactions.tabs.accepted')}</TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed">{t('transactions.tabs.completed')}</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4 mt-6">
            {filterTransactions('all').map(transaction => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
            {filterTransactions('all').length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-gray-500">{t('transactions.noTransactions')}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="pending" className="space-y-4 mt-6">
            {filterTransactions('pending').map(transaction => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
            {filterTransactions('pending').length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-gray-500">{t('transactions.noPending')}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="accepted" className="space-y-4 mt-6">
            {filterTransactions('accepted').map(transaction => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
            {filterTransactions('accepted').length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-gray-500">{t('transactions.noAccepted')}</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4 mt-6">
            {filterTransactions('completed').map(transaction => (
              <TransactionCard key={transaction.id} transaction={transaction} />
            ))}
            {filterTransactions('completed').length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-gray-500">{t('transactions.noCompleted')}</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{t('transactions.reviewDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('transactions.reviewDialog.subtitle')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('transactions.reviewDialog.rating')}</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    data-testid={`rating-star-${star}`}
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`h-8 w-8 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('transactions.reviewDialog.comment')}</label>
              <Textarea
                data-testid="review-comment"
                rows={4}
                placeholder={t('transactions.reviewDialog.commentPlaceholder')}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>{t('transactions.reviewDialog.cancel')}</Button>
            <Button
              data-testid="submit-review-button"
              onClick={handleSubmitReview}
              disabled={loading}
              className="bg-teal-500 hover:bg-teal-600 text-white"
            >
              {loading ? t('transactions.reviewDialog.submitting') : t('transactions.reviewDialog.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}